import Button from '@rio-cloud/rio-uikit/Button';
import FilePicker from '@rio-cloud/rio-uikit/components/filepicker/FilePicker';
import LabeledElement from '@rio-cloud/rio-uikit/LabeledElement';
import Notification from '@rio-cloud/rio-uikit/Notification';
import useClickOutside from '@rio-cloud/rio-uikit/useClickOutside';
import { type ReactElement, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { UploadDocumentTarget } from '#api-bridge/documentApi.ts';
import type { Document } from '#domain/Document.ts';
import { useUploadDocument } from '#features/documents/service/uploadDocument.ts';
import { isFileTooLarge } from '#utils/file.utils.ts';

import { DocumentUploadingProgress } from './DocumentUploadingProgress';

const DropArea = ({ isDragActive }: { isDragActive: boolean }) => (
    <div
        className={`
            rounded border border-style-dashed border-color-gray bg-lightest text-size-14
            text-center text-color-darker opacity-50 padding-25 cursor-pointer min-height-200
            display-grid place-items-center
        `}
    >
        <div>
            <span className='rioglyph rioglyph-files text-size-200pct opacity-50 margin-top-20' />
            <div className='margin-top-10 text-size-18'>
                <FormattedMessage
                    id={
                        isDragActive
                            ? 'intl-msg:ordercom-cloud-frontend.order.documents.upload.dropbody'
                            : 'intl-msg:ordercom-cloud-frontend.order.documents.upload.dropbodyactive'
                    }
                />
            </div>
            <div className='text-color-dark text-italic'>
                <FormattedMessage id={'intl-msg:ordercom-cloud-frontend.order.documents.upload.dropdesc'} />
            </div>
        </div>
    </div>
);

export const DocumentUpload = ({
    onClose,
    associations,
    uploadTarget,
}: {
    onClose: () => void;
    associations: Document['associations'];
    uploadTarget: UploadDocumentTarget;
}) => {
    const ref = useClickOutside(onClose);
    const [file, setFile] = useState<File | undefined>();

    return (
        <div ref={ref} className='display-flex-sm gap-20-sm'>
            <div className='width-100pct max-width-500 display-flex align-items-center overflow-hidden'>
                {file ? (
                    <FilePreview file={file as FileWithPreview} id={0} onDelete={() => setFile(undefined)} />
                ) : (
                    <CustomFilePicker setFile={setFile}>{DropArea}</CustomFilePicker>
                )}
            </div>
            <div className='min-width-200 max-width-200 padding-top-10'>
                <FileInfo
                    file={file}
                    onDelete={() => setFile(undefined)}
                    setFile={setFile}
                    onClose={onClose}
                    associations={associations}
                    uploadTarget={uploadTarget}
                />
            </div>
        </div>
    );
};

type RemoveImageButtonProps = { id: number; onDelete: (id: number) => void };

const RemoveImageButton = ({ id, onDelete = () => {} }: RemoveImageButtonProps) => (
    <Button
        className='text-color-dark hover-text-color-danger'
        bsStyle='muted-filled'
        iconName='rioglyph-trash'
        iconOnly
        autoTrackingKey='ordercom.documents.upload.remove-file'
        onClick={() => onDelete(id)}
    />
);

const CustomFilePicker = ({
    setFile,
    displayMode = 'dropzone',
    children,
}: {
    setFile: React.Dispatch<React.SetStateAction<File | undefined>>;
    displayMode?: 'dropzone' | 'button';
    children?: (props: { isDragActive: boolean }) => ReactElement;
}) => (
    <FilePicker
        label={<FormattedMessage id={'intl-msg:ordercom-cloud-frontend.order.documents.upload.select'} />}
        className={'max-width-100pct'}
        displayMode={displayMode}
        onPick={(acceptedFiles: File[] | null) => {
            const selectedFile = acceptedFiles?.[0];
            if (!selectedFile) {
                setFile(undefined);
                return;
            }
            const fileWithPreview: FileWithPreview = Object.assign(selectedFile, {
                preview: URL.createObjectURL(selectedFile),
            });
            setFile(fileWithPreview);
        }}
        accept={{
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/webp': ['.webp'],
            'application/pdf': ['.pdf'],
        }}
        multiple={false}
    >
        {children}
    </FilePicker>
);

type FileWithPreview = File & { preview: string };
type FilePreviewProps = { id: number; file: FileWithPreview; onDelete: (id: number) => void };

const FilePreview = ({ file }: FilePreviewProps) => {
    const isPDF = file.type === 'application/pdf';

    return (
        <div className={`panel panel-default position-relative overflow-hidden ${isPDF ? 'width-100pct' : ''}`}>
            {isPDF ? (
                <embed src={file.preview as string} type='application/pdf' className='width-100pct min-height-500' />
            ) : (
                <img className='img-responsive' src={file.preview as string} alt={file.name} />
            )}
        </div>
    );
};

const FileInfo = ({
    file,
    onDelete,
    setFile,
    onClose,
    associations,
    uploadTarget,
}: {
    file: File | undefined;
    onDelete: () => void;
    setFile: React.Dispatch<React.SetStateAction<File | undefined>>;
    onClose: () => void;
    associations: Document['associations'];
    uploadTarget: UploadDocumentTarget;
}) => {
    const intl = useIntl();
    const [isUploading, setIsUploading] = useState(false);
    const { upload } = useUploadDocument(uploadTarget);

    const handleCancelUpload = () => {
        setIsUploading(false);
    };

    const handleUpload = async () => {
        if (!file) {
            return;
        }
        setIsUploading(true);
        try {
            await upload({ file, associations });
            Notification.success(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.documents.upload.success',

                    defaultMessage: 'Upload successful',
                })
            );
            onClose();
        } catch (e) {
            console.log('Upload failed');
            console.error('[feature/document-upload] Upload failed.', e);
            Notification.error(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.documents.upload.failure',

                    defaultMessage: 'Upload failed',
                })
            );
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className='display-flex flex-column height-100pct'>
            <div className='text-medium text-size-18 line-height-125rel margin-bottom-5'>
                {intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.common.upload', defaultMessage: 'Upload' })}
            </div>
            <div className='flex-1-1 display-flex'>
                {file && (
                    <div className='display-flex flex-column gap-10 height-100pct width-100pct'>
                        <div className='flex-1-1 display-flex flex-column gap-5'>
                            <LabeledElement
                                label={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.order.documents.filename',

                                    defaultMessage: 'File name',
                                })}
                                noGap
                            >
                                <div className='word-break-all line-height-125rel'>{file.name}</div>
                            </LabeledElement>
                            <LabeledElement
                                label={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.order.documents.filesize',

                                    defaultMessage: 'File size',
                                })}
                                noGap
                            >
                                <div>{(file.size / 1000000).toFixed(2)} mb</div>
                            </LabeledElement>
                        </div>
                        {isFileTooLarge(file) && (
                            <div className={'display-flex text-color-danger'}>
                                <FormattedMessage
                                    id={'intl-msg:ordercom-cloud-frontend.order.documents.filesize.exceeded'}
                                />
                            </div>
                        )}
                        <div className='display-flex flex-colum gap-5'>
                            {isUploading ? (
                                <DocumentUploadingProgress
                                    onCancel={() => {
                                        handleCancelUpload();
                                    }}
                                />
                            ) : (
                                <>
                                    <RemoveImageButton id={0} onDelete={onDelete} />
                                    <Button
                                        className='flex-1-1'
                                        bsStyle='primary'
                                        iconName='rioglyph-upload'
                                        iconRight
                                        autoTrackingKey='ordercom.documents.upload.submit'
                                        onClick={handleUpload}
                                        disabled={isFileTooLarge(file)}
                                    >
                                        {intl.formatMessage({
                                            id: 'intl-msg:ordercom-cloud-frontend.common.upload',

                                            defaultMessage: 'Upload',
                                        })}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                )}
                {!file && (
                    <div className='display-flex flex-column max-width-100pct'>
                        <div className='flex-1-1 text-color-dark white-space-wrap'>
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.common.upload.description',

                                defaultMessage:
                                    'Drag a document onto the area or click the button to select the file from your hard drive.',
                            })}
                        </div>
                        <div>
                            <CustomFilePicker setFile={setFile} displayMode='button' />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
